// components/Survey/QuestionDetailsModal.jsx
import React from 'react';
import { 
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

const QuestionDetailsModal = ({ question, isOpen, onClose }) => {
  const questionTypes = {
    1: { label: 'Rating Scale', color: 'bg-purple-100 text-purple-800' },
    2: { label: 'Text Field', color: 'bg-blue-100 text-blue-800' },
    3: { label: 'Date Field', color: 'bg-green-100 text-green-800' },
    4: { label: 'Text Area', color: 'bg-yellow-100 text-yellow-800' },
    5: { label: 'Radio Buttons', color: 'bg-red-100 text-red-800' },
    6: { label: 'Hidden Field', color: 'bg-gray-100 text-gray-800' },
    7: { label: 'Number Field', color: 'bg-indigo-100 text-indigo-800' },
    8: { label: 'Email Field', color: 'bg-pink-100 text-pink-800' },
    9: { label: 'Checkboxes', color: 'bg-orange-100 text-orange-800' },
    10: { label: 'Select List', color: 'bg-teal-100 text-teal-800' },
    11: { label: 'File Upload', color: 'bg-cyan-100 text-cyan-800' },
    12: { label: 'Net Promoter Score', color: 'bg-lime-100 text-lime-800' },
    13: { label: 'Signature', color: 'bg-amber-100 text-amber-800' },
    14: { label: 'Paragraph', color: 'bg-emerald-100 text-emerald-800' }
  };

  const getTypeConfig = (type) => questionTypes[type] || { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };

  const formatJsonField = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString || '{}');
      return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
      return null;
    }
  };

  if (!question) return null;

  const options = formatJsonField(question.optionsJson);
  const settings = formatJsonField(question.settingsJson);
  const validation = formatJsonField(question.validationRulesJson);
  const style = formatJsonField(question.styleJson);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Question Details" size="lg">
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <QuestionMarkCircleIcon className="w-4 h-4" />
            Basic Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">Question Text</label>
              <p className="text-sm text-gray-900 mt-1">{question.questionText}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Category</label>
              <p className="text-sm text-gray-900 mt-1">{question.categoryName}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Type</label>
              <div className="mt-1">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeConfig(question.type).color}`}>
                  {getTypeConfig(question.type).label}
                </span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Required</label>
              <div className="mt-1 flex items-center gap-2">
                {question.isRequired ? (
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircleIcon className="w-4 h-4 text-gray-400" />
                )}
                <span className="text-sm text-gray-900">
                  {question.isRequired ? 'Required' : 'Optional'}
                </span>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Display Order</label>
              <p className="text-sm text-gray-900 mt-1">{question.order}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Question ID</label>
              <p className="text-sm text-gray-900 mt-1 font-mono">{question.id}</p>
            </div>
          </div>
        </div>

        {/* Help Text & Placeholder */}
        {(question.helpText || question.placeholder) && (
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <InformationCircleIcon className="w-4 h-4" />
              Display Text
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {question.helpText && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Help Text</label>
                  <p className="text-sm text-gray-900 mt-1">{question.helpText}</p>
                </div>
              )}
              {question.placeholder && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Placeholder</label>
                  <p className="text-sm text-gray-900 mt-1">{question.placeholder}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Range Settings */}
        {(question.minValue !== null || question.maxValue !== null) && (
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Range Settings</h4>
            <div className="grid grid-cols-2 gap-4">
              {question.minValue !== null && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Minimum Value</label>
                  <p className="text-sm text-gray-900 mt-1">{question.minValue}</p>
                </div>
              )}
              {question.maxValue !== null && (
                <div>
                  <label className="text-xs font-medium text-gray-500">Maximum Value</label>
                  <p className="text-sm text-gray-900 mt-1">{question.maxValue}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Options */}
        {options && Array.isArray(options) && options.length > 0 && (
          <div className="bg-purple-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Options</h4>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={option.id || index} className="flex items-center gap-3 p-2 bg-white rounded border">
                  <span className="w-6 h-6 flex items-center justify-center bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                    {index + 1}
                  </span>
                  <span className="text-sm text-gray-900">{option.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings */}
        {settings && (
          <div className="bg-yellow-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Settings</h4>
            <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
              {JSON.stringify(settings, null, 2)}
            </pre>
          </div>
        )}

        {/* Validation Rules */}
        {validation && (
          <div className="bg-red-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Validation Rules</h4>
            <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
              {JSON.stringify(validation, null, 2)}
            </pre>
          </div>
        )}

        {/* Style Settings */}
        {style && (
          <div className="bg-indigo-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Style Settings</h4>
            <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
              {JSON.stringify(style, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default QuestionDetailsModal;